import React from "react";
import { Box, Typography, CircularProgress } from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import ReactEChartsCore from "echarts-for-react/lib/core";
import * as echarts from "echarts/core";
import { LineChart } from "echarts/charts";
import {
  GridComponent,
  TooltipComponent,
} from "echarts/components";
import { CanvasRenderer } from "echarts/renderers";
import { EmployeeTrendData } from "@/services/att/services/AnalyticsService";
import { useTranslation } from "react-i18next";

echarts.use([LineChart, GridComponent, TooltipComponent, CanvasRenderer]);

function getEmployeeTrendOption(
  data: EmployeeTrendData,
  labels: { header: string; overtime: string; threshold: string }
) {
  return {
    tooltip: {
      trigger: "axis", backgroundColor: "#FFFFFF",
      borderColor: "#E0E0E0", borderWidth: 1, borderRadius: 8,
      padding: [12, 16], textStyle: { color: "#333333", fontSize: 12 },
      formatter: (params: any) => {
        if (!Array.isArray(params)) return "";
        const week = params[0]?.axisValue ?? "";
        let html = `<div style="font-size:12px;color:#999;margin-bottom:6px;">${labels.header}</div>`;
        params.forEach((p: any) => {
          const color = p.color as string;
          const marker = `<span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:#FFFFFF;border:2px solid ${color};margin-right:6px;"></span>`;
          html += `<div style="display:flex;justify-content:space-between;gap:30px;font-size:13px;font-weight:600;">
            ${marker}${p.seriesName}
            <span style="font-weight:700;">${p.value}</span>
          </div>`;
        });
        return html;
      },
    },
    legend: { show: false },
    grid: { left: 45, right: 20, top: 30, bottom: 30 },
    xAxis: {
      type: "category",
      data: data.weeks,
      axisLine: { lineStyle: { color: "#E0E0E0" } },
      axisTick: { show: false },
      axisLabel: { color: "#999999", fontSize: 12 },
    },
    yAxis: {
      type: "value", min: 0, splitNumber: 6,
      axisLine: { show: false }, axisTick: { show: false },
      splitLine: { lineStyle: { color: "#F0F0F0" } },
      axisLabel: { color: "#999999", fontSize: 12 },
    },
    series: [
      {
        name: labels.overtime, type: "line", smooth: true,
        symbol: "circle", symbolSize: 6,
        lineStyle: { color: "#FF4D4F", width: 2 },
        itemStyle: { color: "#FF4D4F" },
        areaStyle: {
          color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
            { offset: 0, color: "rgba(255, 77, 79, 0.25)" },
            { offset: 1, color: "rgba(255, 77, 79, 0.02)" },
          ]),
        },
        data: data.overtimeEmployees,
      },
      {
        name: labels.threshold, type: "line", smooth: true,
        symbol: "circle", symbolSize: 6,
        lineStyle: { color: "#3B99FC", width: 2 },
        itemStyle: { color: "#3B99FC" },
        areaStyle: {
          color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
            { offset: 0, color: "rgba(59, 153, 252, 0.25)" },
            { offset: 1, color: "rgba(59, 153, 252, 0.02)" },
          ]),
        },
        data: data.thresholdEmployees,
      },
    ],
  };
}

interface Props {
  data: EmployeeTrendData | null;
  loading: boolean;
  onRefresh: () => void;
}

const EmployeeTrendChart: React.FC<Props> = ({ data, loading, onRefresh }) => {
  const { t } = useTranslation();
  const labels = {
    header: t("Dashboard.Employees"),
    overtime: t("Dashboard.Overtime Employees"),
    threshold: t("Dashboard.Threshold Employees"),
  };

  return (
    <Box sx={{ backgroundColor: "#FFFFFF", borderRadius: "12px", border: "1px solid #EBEEF2", p: 1.5, display: "flex", flexDirection: "column", height: "100%" }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.5, flexShrink: 0 }}>
        <Typography sx={{ fontSize: 16, fontWeight: 700, color: "#333333" }}>
          {t("Dashboard.Number of Employees Trend")}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Box sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: "#FF4D4F" }} />
            <Typography sx={{ fontSize: 12, color: "#999999" }}>{labels.overtime}</Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Box sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: "#3B99FC" }} />
            <Typography sx={{ fontSize: 12, color: "#999999" }}>{labels.threshold}</Typography>
          </Box>
          <RefreshIcon
            sx={{ fontSize: 18, color: "#CCCCCC", cursor: "pointer", ml: 0.5 }}
            onClick={onRefresh}
          />
        </Box>
      </Box>
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", flexGrow: 1 }}>
          <CircularProgress size={24} />
        </Box>
      ) : data ? (
        <Box sx={{ flexGrow: 1, minHeight: 0 }}>
          <ReactEChartsCore echarts={echarts} option={getEmployeeTrendOption(data, labels)} style={{ height: "100%" }} notMerge autoResize />
        </Box>
      ) : null}
    </Box>
  );
};

export default EmployeeTrendChart;